import {Checkboxes, DataStatus} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isArray} from 'ramda-adjunct';

import {Nullish, suffixTestId, useOnMount} from 'shared/web';

import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import {useChoiceFilter} from '../../hooks/useChoiceFilter';
import {useLazyChoiceFilterOptions} from '../../hooks/useLazyChoiceFilterOptions';
import {EagFilterProps} from '../../types/EagFilterProps';
import {getQuickFilterFromIntersection} from '../../utils/quickFilterValue';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';

type CheckboxFilterProps = EagFilterProps & {
  columnKey: string;
  quickFilters?: Array<{value: string; label: string}>;
};

export function CheckboxFilter(props: CheckboxFilterProps) {
  const rawModel = props.value[FILTER_VALUE_KEY] as string[] | string | Nullish;

  const [values, setValues] = useChoiceFilter({
    model: rawModel,
    onChange: (newValue) => {
      props.onChange({[FILTER_VALUE_KEY]: newValue});
    },
  });

  const {loadOptions, isLoading, options, errorMessage} = useLazyChoiceFilterOptions({
    columnKey: props.columnKey,
  });

  const isQuickFilterValue = getQuickFilterFromIntersection(values);
  const isControlDisabled = props.isDisabled;

  useOnMount(() => {
    void loadOptions();
  });

  return (
    // spacing keeps the quick-filter chip strip from touching the checkboxes —
    // without it the two rows render with no vertical gap (T20-134467).
    <VStack spacing={2}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={isQuickFilterValue}
        defaultValue={null}
        setFilterValue={setValues}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('checkbox-filter-quickFilters', props)}
      />
      <DataStatus isLoading={isLoading}>
        <Checkboxes
          options={options}
          value={isArray(values) ? values : null}
          onChange={setValues}
          isDisabled={isControlDisabled}
          errorMessage={errorMessage}
          checkedIcon="navigation/check"
          data-testid={suffixTestId('checkbox-filter', props)}
        />
      </DataStatus>
    </VStack>
  );
}
